import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { clientInventoryAPI } from '../../services/api';

const ViewDetailsDialog = ({ open, onClose, tissueId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (open && tissueId) {
      fetchDetails();
    }
  }, [open, tissueId]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await clientInventoryAPI.getDetails(tissueId);
      
      if (response.success) {
        setDetails(response.data);
      } else {
        setError(response.error || 'Failed to load details');
      }
    } catch (err) {
      console.error('Error fetching details:', err);
      setError(err.message || 'Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'UNUSED':
        return 'success';
      case 'USED':
        return 'primary';
      case 'DISCARDED':
        return 'default';
      default:
        return 'default';
    }
  };

  const DetailRow = ({ label, value }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" fontWeight="bold" color="text.secondary">
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">
          {value || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Inventory Details
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : details ? (
          <Box sx={{ pt: 1 }}>
            {/* Status */}
            <Box sx={{ mb: 3 }}>
              <Chip
                label={details.status}
                color={getStatusColor(details.status)}
                sx={{ mb: 1 }}
              />
            </Box>

            {/* Tissue & Product Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Tissue & Product Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <DetailRow label="Tissue ID" value={details.tissueId} />
            <DetailRow label="Product Name" value={details.productName} />
            <DetailRow label="Product SKU" value={details.productSku} />
            <DetailRow label="Batch ID" value={details.batchId} />
            <DetailRow label="Lot Number" value={details.lotNumber} />
            <DetailRow label="Manufacturing Date" value={formatDate(details.manufacturingDate)} />
            <DetailRow label="Expiration Date" value={formatDate(details.expirationDate)} />

            {/* Order Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Order Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <DetailRow label="Order Number" value={details.orderNumber} />
            <DetailRow label="Order Date" value={formatDate(details.orderDate)} />
            <DetailRow label="Shipped Date" value={formatDate(details.shippedDate)} />
            {details.trackingNumber && (
              <DetailRow label="Tracking Number" value={details.trackingNumber} />
            )}

            {/* Usage Information (if USED) */}
            {details.status === 'USED' && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Usage Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailRow label="Date Used" value={formatDate(details.dateUsed)} />
                <DetailRow label="Horse Name" value={details.horseName} />
                <DetailRow label="Injection Site" value={details.injectionSite} />
                {details.notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                      Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {details.notes}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Discard Information (if DISCARDED) */}
            {details.status === 'DISCARDED' && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Discard Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailRow label="Date Discarded" value={formatDate(details.dateDiscarded)} />
                {details.notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                      Reason:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {details.notes}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Product Description */}
            {details.productDescription && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Product Description
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {details.productDescription}
                </Typography>
              </>
            )}

            {/* Timestamps */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Record Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <DetailRow label="Created At" value={formatDate(details.createdAt)} />
            <DetailRow label="Last Updated" value={formatDate(details.updatedAt)} />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDetailsDialog;

